import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Filter, Loader2, Stamp } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Full Passport stamps grid. Backed by GET /api/checkins/mine which
// supports year filtering, status filtering, and paginated envelope.
// Photos are intentionally NOT fetched here (heavy base64 blobs) — only
// the campsite logo/photo enriched on the server is used for the tile.
export default function PassportStamps() {
  const { api } = useAuth();
  const [sp, setSp] = useSearchParams();
  const yearFilter = sp.get("year") || "all";
  const statusFilter = sp.get("status") || "validated";

  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Years the camper actually has stamps in — derived from the response.
  const [years, setYears] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.get("/checkins/mine", {
      params: {
        limit: 500, offset: 0, paginated: true, status: statusFilter,
        year: yearFilter === "all" ? undefined : yearFilter,
      },
    }).then(({ data }) => {
      if (cancelled) return;
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      // Build the years chip strip from *any* checkin_date we saw. When
      // the user filters to a single year, we still want the "all" list
      // available so we can offer other years — so we fetch a slim probe
      // if needed. Cheapest option: derive years from the current page +
      // the year in the query string.
      const ys = new Set(
        (data?.items || [])
          .map((c) => (c.checkin_date || "").slice(0, 4))
          .filter((y) => y && /^\d{4}$/.test(y)),
      );
      if (yearFilter !== "all") ys.add(yearFilter);
      setYears(Array.from(ys).sort().reverse());
    }).catch((e) => {
      if (!cancelled) setError(e?.response?.data?.detail || "Could not load your stamps.");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [api, yearFilter, statusFilter]);

  const setYear = (y) => {
    const next = new URLSearchParams(sp);
    if (y === "all") next.delete("year"); else next.set("year", y);
    setSp(next);
  };
  const setStatus = (s) => {
    const next = new URLSearchParams(sp);
    next.set("status", s);
    setSp(next);
  };

  return (
    <div className="flex flex-col gap-5" data-testid="passport-stamps-page">
      <div>
        <Link to="/passport" className="text-xs font-extrabold uppercase tracking-widest inline-flex items-center gap-1"
              style={{ color: "var(--brand)" }} data-testid="stamps-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Passport
        </Link>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-2" style={{ color: "var(--on-surface)" }}>
          My Passport {total > 0 && <span className="text-base font-bold" style={{ color: "var(--on-surface-muted)" }}>· {total}</span>}
        </h1>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 no-scrollbar">
          <Filter className="w-4 h-4 opacity-60 shrink-0" />
          <FilterChip active={yearFilter === "all"} onClick={() => setYear("all")} testId="year-chip-all">All years</FilterChip>
          {years.map((y) => (
            <FilterChip key={y} active={yearFilter === y} onClick={() => setYear(y)} testId={`year-chip-${y}`}>
              {y}
            </FilterChip>
          ))}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 no-scrollbar">
          {["validated", "pending", "rejected"].map((s) => (
            <FilterChip key={s} active={statusFilter === s} onClick={() => setStatus(s)} testId={`status-chip-${s}`}>
              {s}
            </FilterChip>
          ))}
        </div>
      </div>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}

      {loading && !items && (
        <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your stamps…
        </div>
      )}
      {items && items.length === 0 && (
        <div className="card-elev p-8 text-center" data-testid="stamps-empty">
          <Stamp className="w-8 h-8 mx-auto opacity-40" style={{ color: "var(--on-surface-muted)" }} />
          <div className="mt-2 text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>Nothing to show yet</div>
          <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>Try a different filter, or explore new campsites.</div>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="stamps-grid">
          {items.map((c) => <StampTile key={c.checkin_id} c={c} />)}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick} data-testid={testId}
      className="whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-widest transition shrink-0"
      style={{
        background: active ? "var(--brand)" : "color-mix(in srgb, var(--brand) 10%, transparent)",
        color: active ? "white" : "var(--brand)",
      }}
    >
      {children}
    </button>
  );
}

function StampTile({ c }) {
  const stampImage = c.campsite_stamp_url || c.campsite_logo_data || c.campsite_photo;
  const date = (c.checkin_date || c.created_at || "").slice(0, 10);
  const status = c.status || "validated";
  const statusColor =
    status === "validated" ? "var(--brand)" :
    status === "pending"   ? "var(--premium)" :
                             "#B45309";
  return (
    <Link
      to={`/campsites/${c.campsite_id}`}
      className="card-elev p-3 flex flex-col items-center text-center gap-2 hover:-translate-y-0.5 transition"
      data-testid={`stamp-${c.checkin_id}`}
    >
      <div className="w-full aspect-square rounded-xl flex items-center justify-center overflow-hidden relative"
           style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)" }}>
        {stampImage ? (
          <img src={stampImage} alt="" className="w-full h-full object-cover" />
        ) : (
          <Stamp className="w-8 h-8 opacity-60" style={{ color: "var(--brand)" }} />
        )}
        {status !== "validated" && (
          <span className="absolute top-1.5 right-1.5 text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                style={{ background: statusColor, color: "white" }}>
            {status}
          </span>
        )}
        {c.backdated && (
          <span className="absolute bottom-1.5 left-1.5 text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                style={{ background: "rgba(0,0,0,0.6)", color: "white" }}>
            past
          </span>
        )}
      </div>
      <div className="text-xs font-bold w-full truncate" style={{ color: "var(--on-surface)" }} title={c.campsite_name}>
        {c.campsite_name || "Unknown"}
      </div>
      <div className="text-[10px]" style={{ color: "var(--on-surface-muted)" }}>{date}</div>
    </Link>
  );
}
